/**
 * O cliente HTTP da UI.
 *
 * Único lugar que fala com a API. Duas razões para existir em vez de espalhar
 * `fetch` pelas telas:
 *
 *   1. A autenticação entra aqui e em mais nada. Isso já se pagou: a troca do
 *      token compartilhado por login e senha mexeu neste arquivo e em nenhuma
 *      tela.
 *   2. Erro da API tem formato próprio (`{error:{code,message}}`) e carrega
 *      texto escrito para gente. Desembrulhar num lugar só é o que faz a tela
 *      poder mostrar a frase da API em vez de "erro 400".
 */

import type { CreatedPost, LinhaCalendario } from "../domain/contratos.js";

/** Como a API devolve falha. Ver `errorHandler` em src/http/app.ts. */
interface ApiErrorBody {
  error?: { code?: string; message?: string; detail?: unknown };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly detail?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ------------------------------------------------------------- a sessão
//
// Não há token guardado no navegador, e isso é o ponto.
//
// A sessão vive num cookie `httpOnly`, que o JavaScript desta página não
// consegue ler — nem o nosso, nem o de um XSS. O navegador o envia sozinho, e
// por isso todo `fetch` aqui leva `credentials: "same-origin"`.
//
// O desenho anterior guardava um token compartilhado em `sessionStorage`. Era
// placeholder assumido, e caiu junto com o token: um segredo legível pelo JS
// da página é a chave de publicar na conta de um cliente.

// ---------------------------------------------------------------- chamada

export interface PedidoOpts {
  metodo?: "GET" | "POST" | "PATCH" | "DELETE";
  corpo?: unknown;
  /** Bytes crus, para o upload de mídia. Exclui `corpo`. */
  bytes?: Blob;
}

/**
 * Uma chamada à API, com o token e o desembrulho de erro.
 *
 * Devolve `T`. Lança `ApiError` com a mensagem que a API escreveu — que é
 * texto para humano, e é o que a tela deve mostrar.
 */
export async function api<T>(
  caminho: string,
  opts: PedidoOpts = {}
): Promise<T> {
  const headers: Record<string, string> = {};

  let body: BodyInit | undefined;
  if (opts.bytes) {
    body = opts.bytes;
  } else if (opts.corpo !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.corpo);
  }

  let resp: Response;
  try {
    resp = await fetch(caminho, {
      method: opts.metodo ?? "GET",
      headers,
      body,
      // Sem isto o navegador não manda o cookie de sessão, e toda chamada
      // voltaria 401 mesmo com o login feito.
      credentials: "same-origin"
    });
  } catch (err) {
    // Falha de transporte não tem status nem corpo. Distinguir isso de um erro
    // da API importa: aqui o problema é a rede ou o túnel, não o pedido.
    throw new ApiError(
      `Não foi possível falar com a API. O túnel SSH ainda está aberto? ` +
        `(${err instanceof Error ? err.message : String(err)})`,
      0,
      "network"
    );
  }

  if (resp.status === 204) return undefined as T;

  const texto = await resp.text();
  let dado: unknown;
  try {
    dado = texto ? JSON.parse(texto) : undefined;
  } catch {
    // HTML no lugar de JSON quase sempre é a página de erro de um proxy.
    throw new ApiError(
      `A API respondeu ${resp.status} em formato inesperado.`,
      resp.status,
      "resposta_invalida",
      texto.slice(0, 200)
    );
  }

  if (!resp.ok) {
    const e = (dado as ApiErrorBody)?.error;
    throw new ApiError(
      e?.message ?? `A API respondeu ${resp.status}.`,
      resp.status,
      e?.code ?? "erro",
      e?.detail
    );
  }

  return dado as T;
}

// ---------------------------------------------------------------- formatos
//
// Os tipos que as telas consomem. Escritos à mão a partir das rotas em
// src/http/app.ts — não são gerados, então divergem se a API mudar sem
// alguém mexer aqui. É dívida consciente: gerar exigiria um esquema que o
// projeto não tem.

/**
 * Uma marca, como o seletor do topo a mostra.
 *
 * `sistemaVisual` diz quais modelos o editor de artes oferece para ela — é
 * um conjunto de modelos com nome próprio, não a marca em si, e por isso o
 * editor nunca pergunta "é a Nex Lab?". `null` = a marca ainda não tem
 * modelos.
 */
export interface Marca {
  slug: string;
  nome: string;
  sistemaVisual: string | null;
}

export interface Conta {
  id: number;
  brand: string;
  platform: "instagram" | "linkedin";
  handle: string;
  externalId: string;
  externalParentId: string | null;
  timezone: string;
  isActive: boolean;
  credential: {
    label: string;
    refreshable: boolean;
    expiresAt: string | null;
    lastCheckedAt: string | null;
  };
}

/**
 * Uma linha por DESTINO, não por peça.
 *
 * Importado do contrato compartilhado, e não reescrito aqui. Esta interface
 * já divergiu uma vez — ganhou um `publishedAt` que a API nunca mandou e
 * perdeu dois campos.
 */
export type { LinhaCalendario as DestinoCalendario } from "../domain/contratos.js";

export interface Alarme {
  code: string;
  level: "critical" | "warning";
  message: string;
}

export interface RetratoSaude {
  snapshot: Record<string, number | null>;
  alarms: Alarme[];
}

export interface MidiaEnviada {
  mediaId: number;
  publicUrl: string;
  width: number;
  height: number;
  bytes: number;
  durationMs?: number;
  deduped: boolean;
  sourceFormat: string;
}

// ---------------------------------------------------------------- rotas

export interface MidiaGuardada {
  mediaId: number;
  brand: string;
  kind: "image" | "video";
  mime: string;
  publicUrl: string;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  bytes: number | null;
  createdAt: string;
}

/**
 * Um logo da biblioteca.
 *
 * Note o que NÃO tem aqui: `publicUrl`. O logo não é publicado em CDN
 * nenhum — ele é desenhado dentro da arte, e o que chega ao Instagram é o
 * JPEG final já com a marca embutida. Os bytes vêm de /logos/:id/arquivo,
 * pela nossa origem, autenticados.
 */
export interface LogoGuardado {
  logoId: number;
  nome: string;
  width: number;
  height: number;
  bytes: number;
  createdAt: string;
}

export interface UsuarioLogado {
  id: number;
  email: string;
  name: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export const Api = {
  // ------------------------------------------------------------- sessão
  entrar: (email: string, senha: string) =>
    api<{ usuario: UsuarioLogado }>("/auth/login", {
      metodo: "POST",
      corpo: { email, senha }
    }),

  sair: () => api<{ ok: boolean }>("/auth/logout", { metodo: "POST" }),

  /** Quem sou eu. `viaToken` distingue pessoa de script. */
  quemSou: () =>
    api<{ usuario: UsuarioLogado | null; viaToken: boolean }>("/auth/me"),

  // ----------------------------------------------------------- usuários
  usuarios: () => api<{ users: UsuarioLogado[] }>("/users"),

  criarUsuario: (dados: { email: string; name: string; senha: string }) =>
    api<{ user: UsuarioLogado }>("/users", { metodo: "POST", corpo: dados }),

  definirAtivo: (id: number, isActive: boolean) =>
    api<{ ok: boolean }>(`/users/${id}`, {
      metodo: "PATCH",
      corpo: { isActive }
    }),

  trocarSenha: (id: number, senha: string) =>
    api<{ ok: boolean }>(`/users/${id}`, { metodo: "PATCH", corpo: { senha } }),

  marcas: () => api<{ brands: Marca[] }>("/brands"),

  /** Sem `brand`, todas — é o que a tela de Contas, que é administração, usa. */
  contas: (brand?: string) =>
    api<{ accounts: Conta[] }>(
      `/accounts${brand ? `?brand=${encodeURIComponent(brand)}` : ""}`
    ),

  // ------------------------------------------------------------- logos
  logos: (brand: string) =>
    api<{ logos: LogoGuardado[] }>(`/logos?brand=${encodeURIComponent(brand)}`),

  /**
   * Sobe um logo. Precisa ser PNG, e a API recusa o resto com explicação.
   *
   * JPEG seria aceito por qualquer biblioteca de imagem e traria um retângulo
   * de fundo junto da marca — descoberto só ao olhar a arte pronta. A recusa
   * antecipa isso.
   */
  enviarLogo: (brand: string, arquivo: Blob, nome: string) =>
    api<{
      logoId: number;
      nome: string;
      width: number;
      height: number;
      bytes: number;
      temAlfa: boolean;
      deduped: boolean;
    }>(
      `/logos?brand=${encodeURIComponent(brand)}&nome=${encodeURIComponent(nome)}`,
      {
        metodo: "POST",
        bytes: arquivo
      }
    ),

  removerLogo: (id: number) =>
    api<{ logoId: number; removido: boolean }>(`/logos/${id}`, {
      metodo: "DELETE"
    }),

  /** A biblioteca DA MARCA. Ver src/ui/marca.ts. */
  midias: (brand: string, kind?: "image" | "video") =>
    api<{ media: MidiaGuardada[] }>(
      `/media?brand=${encodeURIComponent(brand)}${kind ? `&kind=${kind}` : ""}`
    ),

  verificarConta: (id: number, dryRun: boolean) =>
    api<{ id: number; verified: boolean; isActive: boolean; dryRun: boolean }>(
      `/accounts/${id}/verify${dryRun ? "?dryRun=true" : ""}`,
      { metodo: "POST" }
    ),

  /**
   * O calendário.
   *
   * Sem `status`, a API esconde os cancelados — destino cancelado é decisão
   * já tomada e nunca mais muda de estado, então empurra para baixo o que
   * ainda vai acontecer. `incluirCancelados` traz tudo.
   */
  calendario: (params: {
    brand?: string;
    from?: string;
    to?: string;
    accountId?: number;
    status?: string;
    incluirCancelados?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params.brand) q.set("brand", params.brand);
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.accountId) q.set("accountId", String(params.accountId));
    if (params.status) q.set("status", params.status);
    if (params.incluirCancelados) q.set("incluirCancelados", "true");
    const s = q.toString();
    return api<{ targets: LinhaCalendario[] }>(`/calendar${s ? `?${s}` : ""}`);
  },

  /**
   * Remove da biblioteca.
   *
   * `removidoDoBucket` diz se o arquivo saiu do S3 de verdade. Hoje vem
   * `false`: a policy não concede `s3:DeleteObject`, e a tela precisa avisar
   * que a URL continua pública em vez de prometer o contrário.
   */
  removerMidia: (id: number) =>
    api<{ mediaId: number; removidoDoBucket: boolean; publicUrl: string }>(
      `/media/${id}`,
      { metodo: "DELETE" }
    ),

  saude: () => api<RetratoSaude>("/health/details"),

  /**
   * Upload de mídia.
   *
   * Vídeo leva `durationMs`, `width` e `height` na query, medidos pelo
   * NAVEGADOR — o servidor não abre o arquivo, por decisão de projeto
   * (ver o bloco VÍDEO em src/media/ingest.ts). É a presença de `durationMs`
   * que faz a API tratar como vídeo.
   */
  enviarMidia: (
    brand: string,
    arquivo: Blob,
    video?: { durationMs: number; width: number; height: number }
  ) => {
    const q = video
      ? `&durationMs=${Math.round(video.durationMs)}&width=${video.width}&height=${video.height}`
      : "";
    return api<MidiaEnviada>(`/media?brand=${encodeURIComponent(brand)}${q}`, {
      metodo: "POST",
      bytes: arquivo
    });
  },

  /**
   * Cria a peça e os destinos, em draft.
   *
   * O retorno usa `CreatedPost` importado do DOMÍNIO, não um tipo escrito à
   * mão aqui. Já divergiu duas vezes — a última mandou `postId` e a UI leu
   * `id`, o que virou "Peça undefined criada" e um 400 no enfileirar. Com o
   * tipo importado, a divergência vira erro de compilação.
   */
  criarPeca: (corpo: {
    format: string;
    body: string | null;
    mediaIds: number[];
    targets: {
      accountId: number;
      scheduledAt: string;
      bodyOverride?: string | null;
    }[];
  }) =>
    api<CreatedPost & { status: string; note: string }>("/posts", {
      metodo: "POST",
      corpo
    }),

  enfileirar: (postId: number) =>
    api<{ id: number; queued: number }>(`/posts/${postId}/queue`, {
      metodo: "POST"
    }),

  /**
   * Apaga a peça inteira. Só funciona se nenhum destino tiver ido ao ar.
   *
   * Peça publicada é histórico do que saiu no perfil — apagá-la faria o
   * sistema esquecer um post que existe, e aí ninguém descobre que ele veio
   * daqui.
   */
  /** Edita a peça. Só enquanto TODOS os destinos dela forem rascunho. */
  editarPeca: (
    postId: number,
    campos: { body?: string | null; format?: string }
  ) =>
    api<{ id: number; updated: boolean }>(`/posts/${postId}`, {
      metodo: "PATCH",
      corpo: campos
    }),

  /**
   * Reagenda UM destino. Só em rascunho.
   *
   * Depois de aprovado o worker pode pegá-lo a qualquer instante — mudar o
   * horário nessa janela é apostar contra o relógio.
   */
  reagendar: (postId: number, targetId: number, scheduledAt: string) =>
    api<{ targetId: number; scheduledAt: string }>(
      `/posts/${postId}/targets/${targetId}`,
      { metodo: "PATCH", corpo: { scheduledAt } }
    ),

  /**
   * Desfaz a aprovação: devolve os destinos em fila para rascunho.
   *
   * Não interrompe publicação em voo — um destino já em `publishing` continua,
   * e a resposta diz que ele ficou de fora.
   */
  desaprovar: (postId: number) =>
    api<{
      id: number;
      devolvidos: number;
      intocados: string[];
      nota: string;
    }>(`/posts/${postId}/unqueue`, { metodo: "POST" }),

  apagarPeca: (postId: number) =>
    api<{ id: number; apagada: boolean; destinos: number }>(
      `/posts/${postId}`,
      { metodo: "DELETE" }
    ),

  cancelarDestino: (postId: number, targetId: number) =>
    api<{ targetId: number; status: string }>(
      `/posts/${postId}/targets/${targetId}`,
      { metodo: "DELETE" }
    )
};
